import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAssessments, createAssessment, publishAssessment, getScoreSummary } from '../services/assessmentService';
import { getProjects } from '../services/projectService';

const CATEGORIES = [
  'infrastructure', 'environment', 'social', 'economic',
  'governance', 'health', 'education', 'culture', 'safety', 'other',
];

const SENTIMENTS = ['positive', 'neutral', 'negative', 'mixed'];

const emptyConcern = { label: '', category: 'other', score: 5, sentiment: 'neutral', notes: '' };

const AssessmentsPage = () => {
  const { activeTenant } = useAuth();
  const tenantId = activeTenant?._id;
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [assessments, setAssessments] = useState([]);
  const [scoreSummary, setScoreSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');

  const [form, setForm] = useState({
    title: '',
    description: '',
    summary: '',
    responseScope: 'all',
    tags: '',
    concerns: [{ ...emptyConcern }],
  });

  useEffect(() => {
    if (tenantId) {
      getProjects(tenantId).then((res) => setProjects(res.data)).catch(console.error);
    }
  }, [tenantId]);

  const loadProjectData = async (projectId) => {
    if (!projectId || !tenantId) return;
    setLoading(true);
    try {
      const [aRes, sRes] = await Promise.all([
        getAssessments({ tenantId, projectId }),
        getScoreSummary({ tenantId, projectId }),
      ]);
      setAssessments(aRes.data);
      setScoreSummary(sRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (e) => {
    const id = e.target.value;
    setSelectedProject(id);
    setAssessments([]);
    setScoreSummary(null);
    if (id) loadProjectData(id);
  };

  const handleFormChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleConcernChange = (index, field, value) => {
    const updated = form.concerns.map((c, i) => (i === index ? { ...c, [field]: value } : c));
    setForm({ ...form, concerns: updated });
  };

  const addConcern = () => setForm({ ...form, concerns: [...form.concerns, { ...emptyConcern }] });

  const removeConcern = (index) =>
    setForm({ ...form, concerns: form.concerns.filter((_, i) => i !== index) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        tenantId,
        projectId: selectedProject,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()) : [],
        concerns: form.concerns.filter((c) => c.label.trim()),
      };
      const res = await createAssessment(payload);
      setAssessments((prev) => [res.data, ...prev]);
      setForm({ title: '', description: '', summary: '', responseScope: 'all', tags: '', concerns: [{ ...emptyConcern }] });
      setShowForm(false);
      setMsg('Assessment created.');
      loadProjectData(selectedProject);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to create assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async (id) => {
    try {
      const res = await publishAssessment(id);
      setAssessments((prev) => prev.map((a) => (a._id === id ? res.data : a)));
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to publish');
    }
  };

  return (
    <div className="page assessments-page">
      <h2>Assessment Engine</h2>

        <div className="project-selector">
          <select value={selectedProject} onChange={handleProjectChange}>
            <option value="">-- Select a Project --</option>
            {projects.map((p) => <option key={p._id} value={p._id}>{p.title}</option>)}
          </select>
        </div>

        {scoreSummary && scoreSummary.totalAssessments > 0 && (
          <section className="score-summary-panel">
            <h3>Score Summary</h3>
            <p>Published Assessments: <strong>{scoreSummary.totalAssessments}</strong></p>
            <p>Average Overall Score: <strong>{scoreSummary.averageOverallScore} / 10</strong></p>
            {scoreSummary.categoryDistribution.length > 0 && (
              <div className="category-dist">
                <h4>Category Distribution</h4>
                <div className="category-pills">
                  {scoreSummary.categoryDistribution.map((cat) => (
                    <span key={cat._id} className={`category-pill cat-${cat._id}`}>
                      {cat._id} ({cat.count})
                    </span>
                  ))}
                </div>
              </div>
            )}
            {scoreSummary.categoryBreakdown.length > 0 && (
              <div className="category-scores">
                <h4>Scores by Category</h4>
                <table className="report-table">
                  <thead><tr><th>Category</th><th>Avg Score</th><th>Concern Count</th></tr></thead>
                  <tbody>
                    {scoreSummary.categoryBreakdown.map((row) => (
                      <tr key={row._id}>
                        <td>{row._id}</td>
                        <td>{row.avgScore.toFixed(2)}</td>
                        <td>{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {selectedProject && (
          <>
            {msg && <p className={msg.includes('Failed') ? 'error-msg' : 'success-msg'}>{msg}</p>}
            <button className="btn" onClick={() => setShowForm(!showForm)}>
              {showForm ? 'Cancel' : '+ New Assessment'}
            </button>

            {showForm && (
              <form className="assessment-form" onSubmit={handleSubmit}>
                <h3>New Assessment</h3>
                <input name="title" placeholder="Assessment Title" value={form.title} onChange={handleFormChange} required />
                <textarea name="description" placeholder="Description" value={form.description} onChange={handleFormChange} />
                <textarea name="summary" placeholder="Written Summary" value={form.summary} onChange={handleFormChange} />
                <input name="tags" placeholder="Tags (comma-separated)" value={form.tags} onChange={handleFormChange} />
                <select name="responseScope" value={form.responseScope} onChange={handleFormChange}>
                  <option value="all">All Responses</option>
                  <option value="public">Public Only</option>
                  <option value="community_only">Community Only</option>
                  <option value="community_plus_platform">Community + Platform</option>
                </select>

                <h4>Concerns</h4>
                {form.concerns.map((concern, i) => (
                  <div key={i} className="concern-row">
                    <input
                      placeholder="Concern label"
                      value={concern.label}
                      onChange={(e) => handleConcernChange(i, 'label', e.target.value)}
                    />
                    <select value={concern.category} onChange={(e) => handleConcernChange(i, 'category', e.target.value)}>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={concern.sentiment} onChange={(e) => handleConcernChange(i, 'sentiment', e.target.value)}>
                      {SENTIMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input
                      type="number" min="0" max="10" step="0.5"
                      placeholder="Score (0-10)"
                      value={concern.score}
                      onChange={(e) => handleConcernChange(i, 'score', parseFloat(e.target.value))}
                    />
                    <input
                      placeholder="Notes"
                      value={concern.notes}
                      onChange={(e) => handleConcernChange(i, 'notes', e.target.value)}
                    />
                    {form.concerns.length > 1 && (
                      <button type="button" className="btn-danger btn-sm" onClick={() => removeConcern(i)}>Remove</button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn-secondary" onClick={addConcern}>+ Add Concern</button>

                <div className="form-actions">
                  <button type="submit" disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save Assessment'}
                  </button>
                </div>
              </form>
            )}

            <h3>Assessments</h3>
            {loading ? (
              <p>Loading...</p>
            ) : assessments.length === 0 ? (
              <p>No assessments yet for this project.</p>
            ) : (
              <div className="assessment-list">
                {assessments.map((a) => (
                  <div key={a._id} className={`assessment-card status-${a.status}`}>
                    <div className="assessment-header">
                      <h4>{a.title}</h4>
                      <span className={`status-badge status-${a.status}`}>{a.status}</span>
                      <span className="score-badge">Score: {a.overallScore}/10</span>
                    </div>
                    {a.summary && <p className="assessment-summary">{a.summary}</p>}
                    {a.concerns.length > 0 && (
                      <div className="concern-pills">
                        {a.concerns.map((c, i) => (
                          <span key={i} className={`concern-pill cat-${c.category}`}>
                            {c.label} ({c.score}/10)
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="assessment-meta">
                      <span>Categories: {a.categories.join(', ') || 'none'}</span>
                      {a.tags.length > 0 && <span>Tags: {a.tags.join(', ')}</span>}
                    </div>
                    {a.status !== 'published' && (
                      <button className="btn-sm" onClick={() => handlePublish(a._id)}>Publish</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
  );
};

export default AssessmentsPage;
